<!-- eslint-disable max-lines -->
<!-- eslint-disable @typescript-eslint/ban-ts-comment -->
<script setup lang="ts">
import { accessDataBasedOnNetwork } from '@/accessors/common/dataAccessorBasedOnNetwork'
import {
  style as featureStyle,
  type CustomFeature,
  getDownloadedDistrictId,
  highlightFeature,
  resetHighlightFeature
} from '@/libs/leaflet/featureManager'
import { getUserAttributesFromStore } from '@/modules/miiaFw/MiiaFwCognito'
import { setLoadingView } from '@/modules/miiaFw/utils/miiaFwUtils'
import { useAdminDistrictsStore } from '@/stores/adminDistrictsStore'
import { useNetworkStore } from '@/stores/networkStore'
import { useOfflineDataDownloadProcessStore } from '@/stores/offlineDataDownloadProcessStore'
import type { ResQueryAdminDistricts } from '@/types/accessor/api/adminDistricts/adminDistricts'
import CommonModal from '@/components/common/dialog/CommonModal.vue'
import L from 'leaflet'

import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/GlobalError'

const emit = defineEmits(['selectedFeature'])

const offlineDataDownloadProcessStore = useOfflineDataDownloadProcessStore()
const networkStore = useNetworkStore()
const adminDistrictsStore = useAdminDistrictsStore()
const commonModalStore = useCommonModalStore()

interface LeafletMap {
  map: L.Map | null
}
const mapId = 'map-offline-manager'
// デフォルトの緯度
let defaultLat = 35.6896667484302
// デフォルトの経度
let defaultLng = 139.6918005385742
const leaflet = ref<LeafletMap>({
  map: null
})
const cityName = ref<string>('-')
// 選択済みのエリア
const allLayers: L.Layer[] = []
const selectedFeature = ref<CustomFeature[]>([])
let downloadedDistrictIds: string[] = []

// ダウンロード済みデータの初期化
const resetSelectedFeature = async () => {
  selectedFeature.value = []
  downloadedDistrictIds = await getDownloadedDistrictId()
}

/**
 * マップの表示
 * @param なし
 * @returns なし
 */
const createMap = async () => {
  if (leaflet.value.map) {
    return
  }
  setLoadingView(true)
  const tempMap = L.map(mapId)
  // ダウンロード済みの地点を取得
  downloadedDistrictIds = await getDownloadedDistrictId()

  tempMap.addLayer(
    L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png', {
      // 出典はleafletの機能で埋め込まれる関係でclassを指定してcssを充てるのではなく、直接styleを記載して反映させる。
      attribution: `
            <a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank' style="font-size:9px">地理院タイル</a>
            <span> | </span>
            <a href='https://nlftp.mlit.go.jp/cgi-bin/isj/dls/_choose_method.cgi' target='_blank' style="font-size:9px">国土交通省 (測量法に基づく国土地理院長承認（使用）R 5JHs 96)</a>
            <span> | </span>
            <a
              href='https://www.e-stat.go.jp/gis/statmap-search?page=1&type=2&aggregateUnitForBoundary=A&toukeiCode=00200521&toukeiYear=2015&serveyId=A002005212015&coordsys=1&format=gml'
              target='_blank'
              style="font-size:9px">
                政府統計の総合窓口(e-Stat)
            </a>
            `,
      maxZoom: 22,
      minZoom: 2,
      // 地図タイルの最大レベルの指定
      maxNativeZoom: 18
    })
  )

  L.control.scale({ position: 'bottomright', maxWidth: 200, imperial: false }).addTo(tempMap)
  tempMap.invalidateSize()
  leaflet.value.map = tempMap
  const geoJsonFeatureCollection = await createGeoJsonFeatureCollection().finally(() => {
    setLoadingView(false)
    const centerLatLang = [defaultLat, defaultLng] as L.LatLngTuple
    tempMap.setView(centerLatLang, 13)
  })
  // TODO typeScriptのエラーがどうしても解消できないためts-ignoreを利用
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  L.geoJson<GeoJSON.Geometry, StreetProperties>(geoJsonFeatureCollection as any, {
    // @ts-ignore
    style: featureStyle,
    // @ts-ignore
    onEachFeature: customOnEachFeature(selectDistrict)
  }).addTo(tempMap)
}

/**
 * 区の選択イベント
 * @param e イベント
 * @returns なし
 */
const selectDistrict = (e: L.LeafletMouseEvent): void => {
  const isSelectedAllFeature = offlineDataDownloadProcessStore.isSelectedAllFeature
  if (isSelectedAllFeature) return
  const layer = e.target as L.Path
  if (!layer) return
  if ('feature' in layer) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const feature = (layer as any).feature as CustomFeature
    const selected = selectedFeature.value.findIndex(
      item => item.properties.districtId === feature.properties.districtId
    )
    if (selected !== -1) {
      resetHighlightFeature(layer)
      selectedFeature.value.splice(selected, 1)
      // クリックされた地区名と同じ名前の地区を探し、選択解除する
      const clickedDistrictName = feature.properties.streetName
      allLayers.forEach(layer => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const layerFeature = (layer as any).feature as CustomFeature
        if (
          layerFeature.properties.streetName === clickedDistrictName &&
          selectedFeature.value.find(
            item => item.properties.districtId === layerFeature.properties.districtId
          )
        ) {
          resetHighlightFeature(layer as L.Path)
          const index = selectedFeature.value.findIndex(
            item => item.properties.districtId === layerFeature.properties.districtId
          )
          if (index !== -1) {
            selectedFeature.value.splice(index, 1)
          }
        }
      })
    } else {
      highlightFeature(layer)
      selectedFeature.value.push(feature)
      // クリックされた地区名と同じ名前の地区を探し、ハイライトする
      const clickedDistrictName = feature.properties.streetName
      allLayers.forEach(layer => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const layerFeature = (layer as any).feature as CustomFeature
        if (layerFeature.properties.streetName === clickedDistrictName) {
          highlightFeature(layer as L.Path)
          if (
            !selectedFeature.value.find(
              item => item.properties.districtId === layerFeature.properties.districtId
            )
          ) {
            selectedFeature.value.push(layerFeature)
          }
        }
      })
    }
  }
}

/**
 * 全ての区を選択する
 * @param なし
 * @returns なし
 */
const selectAllDistrict = () => {
  for (const d of allLayers) {
    const layer = d as L.Path
    layer.setStyle({
      weight: 1,
      fillColor: '#7fffd4',
      dashArray: '',
      fillOpacity: 0.3,
      opacity: 1
    })

    if ('feature' in layer) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const feature = (layer as any).feature as CustomFeature
      const selected = selectedFeature.value.findIndex(
        item => item.properties.districtId === feature.properties.districtId
      )
      if (selected === -1) {
        selectedFeature.value.push(feature)
      }
    }
  }
}

/**
 * 全ての区を選択解除する
 * @param includeDownloaded ダウンロード済みの区域も選択解除の対象に含めるかどうか
 * @returns なし
 */
const resetAllDistrict = (includeDownloaded: boolean = false) => {
  for (const d of allLayers) {
    const layer = d as L.Path
    if ('feature' in layer) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const feature = (layer as any).feature as CustomFeature
      if (!downloadedDistrictIds.includes(feature.properties.districtId)) {
        resetHighlightFeature(layer)
        const selected = selectedFeature.value.findIndex(
          item => item.properties.districtId === feature.properties.districtId
        )
        if (selected !== -1) {
          selectedFeature.value.splice(selected, 1)
        }
      } else if (includeDownloaded) {
        resetHighlightFeature(layer)
        const selected = selectedFeature.value.findIndex(
          item => item.properties.districtId === feature.properties.districtId
        )
        if (selected !== -1) {
          selectedFeature.value.splice(selected, 1)
        }
      }
    }
  }
}

/**
 * 区画情報を取得する
 * @param なし
 * @returns なし
 */
const createGeoJsonFeatureCollection = async (): Promise<GeoJSON.FeatureCollection> => {
  const attribute = await getUserAttributesFromStore()
  const searchParam = {
    govCode: attribute.attribute['disaster-commonattr'].govCode,
    type: 'street'
  }
  const res = await accessDataBasedOnNetwork(
    'queryAdminDistrictsDaoApiOnlineOnly',
    searchParam,
    networkStore.getIsOnline,
    () => {
      commonModalStore.openConfirmModal({
        title: '行政区画の取得に失敗しました',
        body: 'サーバーとの接続に失敗しました。<br>通信状態を確認して再度実行してください。',
        okTitle: '閉じる'
      })
    }
  )
    .then(res => res as ResQueryAdminDistricts)
    .catch(err => {
      console.error(err)
      throw new GlobalError([ERROR_CODES.MAP_DOWNLOAD_ADMIN_DISTRICTS_GET_FAILED])
    })
  adminDistrictsStore.updateStreetState(res)
  const features: GeoJSON.Feature[] = (() => {
    const ret: GeoJSON.Feature[] = []
    // 区の中心点を計算する
    const logArray: number[] = []
    const latArray: number[] = []
    for (const data of res.result) {
      logArray.push(data.location[0])
      latArray.push(data.location[1])
      const featureObj: CustomFeature = {
        // 面データ
        type: 'Feature',
        properties: {
          districtId: data.districtId,
          streetName: data.streetName,
          selected: false
        },
        geometry: {
          type: 'Polygon',
          coordinates: data.shape.coordinates[0]
        }
      }
      ret.push(featureObj)
    }
    cityName.value = res.result[0].cityName
    // 平均値を中心に描画
    defaultLng = logArray.reduce((acc, val) => acc + val, 0) / logArray.length
    defaultLat = latArray.reduce((acc, val) => acc + val, 0) / latArray.length
    return ret
  })()

  return {
    type: 'FeatureCollection',
    features: features
  }
}

/**
 *  leafletのInteractive Choroplethの設定
 * @param e イベント
 * @returns なし
 */
const customOnEachFeature = (clickEventHandler: (e: L.LeafletMouseEvent) => void) => {
  selectedFeature.value = []
  return (feature: CustomFeature, layer: L.Layer): void => {
    allLayers.push(layer)
    // ダウンロード済みのエリアをハイライトする
    if (downloadedDistrictIds.includes(feature.properties.districtId)) {
      highlightFeature(layer as L.Path)
      selectedFeature.value.push(feature)
    }

    // 地区名表示の要素を生成
    const b = (layer as L.Polygon).getBounds()
    const center = b.getCenter()
    const divIcon = L.divIcon({
      className: 'area-info',
      html: `${feature.properties.streetName}`,
      iconSize: [80, 30]
    })
    const maker = L.marker(center, { icon: divIcon }).addTo(leaflet.value.map as L.Map)
    // touchイベントが用意されていないためtouchStartとtouchEndイベントの時間差から二重で動作することを防止
    let startTouchTime: number = 0
    // 地区名を押下してもハイライトするようクリックイベントを設定
    maker.on('click', () => {
      if (startTouchTime === 0) {
        startTouchTime = performance.now()
        const isSelectedAllFeature = offlineDataDownloadProcessStore.isSelectedAllFeature
        if (isSelectedAllFeature) return
        const _layer = layer as L.Path
        if ('feature' in layer) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const feature = (layer as any).feature as CustomFeature
          const selected = selectedFeature.value.findIndex(
            item => item.properties.districtId === feature.properties.districtId
          )
          if (selected !== -1) {
            resetHighlightFeature(_layer)
            selectedFeature.value.splice(selected, 1)
            // クリックされた地区名と同じ名前の地区を探し、選択解除する
            const clickedDistrictName = feature.properties.streetName
            allLayers.forEach(layer => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const layerFeature = (layer as any).feature as CustomFeature
              if (
                layerFeature.properties.streetName === clickedDistrictName &&
                selectedFeature.value.find(
                  item => item.properties.districtId === layerFeature.properties.districtId
                )
              ) {
                resetHighlightFeature(layer as L.Path)
                const index = selectedFeature.value.findIndex(
                  item => item.properties.districtId === layerFeature.properties.districtId
                )
                if (index !== -1) {
                  selectedFeature.value.splice(index, 1)
                }
              }
            })
          } else {
            highlightFeature(_layer)
            selectedFeature.value.push(feature)
            // クリックされた地区名と同じ名前の地区を探し、ハイライトする
            const clickedDistrictName = feature.properties.streetName
            allLayers.forEach(layer => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const layerFeature = (layer as any).feature as CustomFeature
              if (layerFeature.properties.streetName === clickedDistrictName) {
                highlightFeature(layer as L.Path)
                if (
                  !selectedFeature.value.find(
                    item => item.properties.districtId === layerFeature.properties.districtId
                  )
                ) {
                  selectedFeature.value.push(layerFeature)
                }
              }
            })
          }
          return
        }
      }
      const endTime = performance.now()
      const executionTime = endTime - startTouchTime
      if (executionTime > 500) {
        startTouchTime = 0
      }
    })
    // レイヤーのイベントを設定
    layer.on({
      mouseover: e => {
        const layer = e.target as L.Path
        layer.setStyle({
          fillColor: '#7fffd4',
          fillOpacity: 0.3
        })
      },
      mouseout: e => {
        const layer = e.target as L.Path
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const feature = (layer as any).feature as CustomFeature
        const selected = selectedFeature.value.findIndex(
          item => item.properties.districtId === feature.properties.districtId
        )
        if (selected === -1) {
          layer.setStyle({
            fillColor: '#ffffff',
            fillOpacity: 0.3
          })
        }
      },
      click: e => {
        if (startTouchTime === 0) {
          startTouchTime = performance.now()
          clickEventHandler(e)
          return
        }
        const endTime = performance.now()
        const executionTime = endTime - startTouchTime
        if (executionTime > 500) {
          startTouchTime = 0
        }
      }
    })
  }
}

watch(selectedFeature, newValue => {
  emit('selectedFeature', newValue)
})
// ライフサイクル
onMounted(async () => {})
onBeforeUnmount(() => {})

defineExpose({
  leaflet,
  cityName,
  resetSelectedFeature,
  createMap,
  selectDistrict,
  createGeoJsonFeatureCollection,
  selectAllDistrict,
  resetAllDistrict
})
</script>

<template>
  <div>
    <div class="map-container" ref="map-offline-manager" id="map-offline-manager"></div>
    <CommonModal />
  </div>
</template>

<style>
.leaflet-container {
  z-index: 0;
}

#map-offline-manager {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.area-info {
  font-weight: bold;
}
</style>
